import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { PanelTop, Rows } from 'lucide-react'

function ThreeCharts({ size = 20, strokeWidth = 1.2, ...props }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} aria-hidden {...props}>
    <rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 12h18M12 12v9" />
  </svg>
}
function ThreeChartsTop({ size = 20, strokeWidth = 1.2, ...props }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} aria-hidden {...props}>
    <rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 12h18M12 3v9" />
  </svg>
}
function FourCharts({ size = 20, strokeWidth = 1.2, ...props }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} aria-hidden {...props}>
    <rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 12h18M10 3v9M14 12v9" />
  </svg>
}
const LAYOUTS = [['single', 'Single chart', PanelTop], ['split', 'Two stacked charts', Rows],
  ['triple', 'Three charts', ThreeCharts], ['tripleTop', 'Two above, one below', ThreeChartsTop], ['quad', 'Four charts', FourCharts]]

export default function ChartLayoutMenu({ layout, syncCrosshair, onLayoutChange, onSyncCrosshairChange, disabled }) {
  const buttonRef = useRef(null)
  const menuRef = useRef(null)
  const [position, setPosition] = useState(null)
  useEffect(() => {
    if (!position) return undefined
    const closeOutside = (event) => {
      if (!buttonRef.current?.contains(event.target) && !menuRef.current?.contains(event.target)) setPosition(null)
    }
    const closeEscape = (event) => {
      if (event.key === 'Escape') { setPosition(null); buttonRef.current?.focus() }
    }
    const close = () => setPosition(null)
    window.addEventListener('pointerdown', closeOutside)
    window.addEventListener('keydown', closeEscape)
    window.addEventListener('resize', close)
    window.addEventListener('scroll', close, true)
    return () => {
      window.removeEventListener('pointerdown', closeOutside)
      window.removeEventListener('keydown', closeEscape)
      window.removeEventListener('resize', close)
      window.removeEventListener('scroll', close, true)
    }
  }, [position])
  const Icon = LAYOUTS.find(([value]) => value === layout)?.[2] || PanelTop
  return <>
    <button ref={buttonRef} type="button" aria-label="Chart layout" title="Chart layout" disabled={disabled}
      aria-haspopup="dialog" aria-expanded={Boolean(position)}
      className="p-2 text-inkMuted hover:text-ink disabled:opacity-40"
      onClick={() => {
        if (position) { setPosition(null); return }
        const rect = buttonRef.current.getBoundingClientRect()
        setPosition({ top: rect.bottom + 6, left: Math.max(8, Math.min(rect.right - 220, window.innerWidth - 228)) })
      }}><Icon size={14} strokeWidth={1.4} aria-hidden /></button>
    {position && createPortal(<div ref={menuRef} role="dialog" aria-label="Chart layout settings"
      className="fixed z-[100] w-[220px] rounded-lg border border-border bg-canvas p-3 text-ink shadow-lg"
      style={position}>
      <div className="flex gap-2" role="group" aria-label="Layout">
        {LAYOUTS.map(([value, label, LayoutIcon]) =>
          <button key={value} type="button" aria-label={label} data-command-label={label} data-command-id={`position.chart.layout.${value === 'tripleTop' ? 'triple_top' : value}`} aria-pressed={layout === value}
            className={`rounded p-2 border ${layout === value ? 'border-inkMuted bg-canvas2' : 'border-transparent hover:bg-canvas2'}`}
            onClick={() => { onLayoutChange(value); setPosition(null) }}><LayoutIcon size={20} strokeWidth={1.2} /></button>)}
      </div>
      <p className="mt-2 text-[10px] text-inkMuted">Option + double-click a chart to maximize or restore it.</p>
      <div className="mt-3 border-t border-border pt-3">
        <div className="mb-2 text-[10px] uppercase tracking-wider text-inkDim">Sync in layout</div>
        <button type="button" role="switch" aria-label="Sync crosshair" aria-checked={syncCrosshair}
          className="flex w-full items-center justify-between text-xs text-inkMuted"
          onClick={() => onSyncCrosshairChange(!syncCrosshair)}>
          <span>Crosshair</span>
          <span aria-hidden className={`flex h-4 w-7 items-center rounded-full px-0.5 ${syncCrosshair ? 'bg-inkMuted justify-end' : 'bg-canvas2 justify-start'}`}>
            <span className="h-3 w-3 rounded-full bg-ink" />
          </span>
        </button>
      </div>
    </div>, document.body)}
  </>
}
